import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from '@/hooks/useDebounce';
import { AgentCard } from './AgentCard';
import { FeaturedAgents } from './FeaturedAgents';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from '@/lib/utils';
import { 
  Search, 
  SlidersHorizontal, 
  Grid2x2, 
  List, 
  X, 
  RefreshCw,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { GalleryFilters, ShowcaseItem } from '@/types/showcase';
import { showcaseService } from '@/services/showcase/showcaseService';
import { interactionService } from '@/services/showcase/interactionService';
import { useAuth } from '@/contexts/auth/AuthContext';

interface ShowcaseGalleryProps {
  showFeatured?: boolean;
  defaultItemType?: 'agent' | 'project' | 'template';
  className?: string;
}

/**
 * Main showcase gallery component for displaying and filtering showcase items
 */
export const ShowcaseGallery: React.FC<ShowcaseGalleryProps> = ({
  showFeatured = true,
  defaultItemType = 'agent',
  className,
}) => {
  // State management
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<ShowcaseItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [userLikes, setUserLikes] = useState<Record<string, boolean>>({});
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const { user } = useAuth();

  // Filters state
  const [filters, setFilters] = useState<GalleryFilters>({
    search: searchParams.get('search') || '',
    tags: searchParams.getAll('tag'),
    capabilities: searchParams.getAll('capability'),
    complexity: (searchParams.getAll('complexity') as any) || [],
    itemType: searchParams.has('type') 
      ? [(searchParams.get('type') as 'agent' | 'project' | 'template')]
      : [defaultItemType],
    sortBy: (searchParams.get('sort') as GalleryFilters['sortBy']) || 'newest',
    featured: searchParams.get('featured') === 'true',
    limit: 12,
    offset: parseInt(searchParams.get('page') || '0') * 12,
  });

  // Debounce search to avoid too many requests
  const debouncedSearch = useDebounce(filters.search, 500);

  // Fetch showcase items based on filters
  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const result = await showcaseService.getShowcaseItems({
        ...filters,
        search: debouncedSearch,
      });
      setItems(result.items);
      setTotal(result.total);
      setError(null);

      // If user is logged in, check which items they've liked
      if (user) {
        const interactions = await interactionService.getUserInteractions(user.id, 'like');
        const likesMap: Record<string, boolean> = {};
        interactions.forEach(interaction => {
          likesMap[interaction.itemId] = true;
        });
        setUserLikes(likesMap);
      }
    } catch (err) {
      console.error('Error fetching showcase items:', err);
      setError('Failed to load showcase items. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [filters, debouncedSearch, user]);

  // Update URL search params when filters change
  useEffect(() => {
    const newParams = new URLSearchParams();
    
    if (filters.search) newParams.set('search', filters.search);
    if (filters.sortBy) newParams.set('sort', filters.sortBy);
    if (filters.featured) newParams.set('featured', 'true');
    if (filters.offset > 0) newParams.set('page', String(Math.floor(filters.offset / filters.limit)));
    
    filters.tags.forEach(tag => newParams.append('tag', tag));
    filters.capabilities.forEach(capability => newParams.append('capability', capability));
    filters.complexity.forEach(complexity => newParams.append('complexity', complexity));
    
    if (filters.itemType.length === 1 && filters.itemType[0] !== defaultItemType) {
      newParams.set('type', filters.itemType[0]);
    }
    
    setSearchParams(newParams, { replace: true });
  }, [filters, setSearchParams, defaultItemType]);

  // Fetch items when filters or search changes
  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Handle search input
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({ ...prev, search: e.target.value, offset: 0 }));
  };

  // Handle sort change
  const handleSortChange = (value: GalleryFilters['sortBy']) => {
    setFilters(prev => ({ ...prev, sortBy: value, offset: 0 }));
  };

  // Handle filter changes
  const handleFilterChange = (filterType: keyof GalleryFilters, value: any) => {
    setFilters(prev => ({ ...prev, [filterType]: value, offset: 0 }));
  };

  // Handle tag toggle
  const handleTagToggle = (tag: string) => {
    setFilters(prev => {
      const tags = prev.tags || [];
      return {
        ...prev,
        tags: tags.includes(tag) 
          ? tags.filter(t => t !== tag) 
          : [...tags, tag],
        offset: 0
      };
    });
  };

  // Handle capability toggle
  const handleCapabilityToggle = (capability: string) => {
    setFilters(prev => {
      const capabilities = prev.capabilities || [];
      return {
        ...prev,
        capabilities: capabilities.includes(capability) 
          ? capabilities.filter(c => c !== capability) 
          : [...capabilities, capability],
        offset: 0
      };
    });
  };

  // Handle complexity toggle
  const handleComplexityToggle = (complexity: 'beginner' | 'intermediate' | 'advanced') => {
    setFilters(prev => {
      const complexities = prev.complexity || [];
      return {
        ...prev,
        complexity: complexities.includes(complexity) 
          ? complexities.filter(c => c !== complexity) 
          : [...complexities, complexity],
        offset: 0
      };
    });
  };

  // Handle featured toggle
  const handleFeaturedToggle = () => {
    setFilters(prev => ({
      ...prev,
      featured: !prev.featured,
      offset: 0
    }));
  };

  // Handle pagination
  const handlePageChange = (direction: 'next' | 'prev') => {
    setFilters(prev => {
      if (direction === 'prev' && prev.offset >= prev.limit) {
        return { ...prev, offset: prev.offset - prev.limit };
      } else if (direction === 'next' && prev.offset + prev.limit < total) {
        return { ...prev, offset: prev.offset + prev.limit };
      }
      return prev;
    });
  };

  // Handle item type change
  const handleItemTypeChange = (type: 'agent' | 'project' | 'template') => {
    setFilters(prev => ({
      ...prev,
      itemType: [type],
      offset: 0
    }));
  };

  // Handle reset filters
  const handleResetFilters = () => {
    setFilters({
      search: '',
      tags: [],
      capabilities: [],
      complexity: [],
      itemType: [defaultItemType],
      sortBy: 'newest',
      featured: false,
      limit: 12,
      offset: 0,
    });
  };

  // Interaction handlers
  const handleLike = async (id: string) => {
    if (!user) return;
    
    try {
      const isLiked = userLikes[id];
      
      if (isLiked) {
        await interactionService.removeInteraction(user.id, id, 'like');
        setUserLikes(prev => ({ ...prev, [id]: false }));
        
        // Update like count in the UI
        setItems(prev => 
          prev.map(item => 
            item.id === id ? { ...item, likes: Math.max(0, (item.likes || 0) - 1) } : item
          )
        );
      } else {
        await interactionService.recordInteraction({
          userId: user.id,
          itemId: id,
          interactionType: 'like',
        });
        setUserLikes(prev => ({ ...prev, [id]: true }));
        
        // Update like count in the UI
        setItems(prev => 
          prev.map(item => 
            item.id === id ? { ...item, likes: (item.likes || 0) + 1 } : item
          )
        );
      }
    } catch (err) {
      console.error('Error toggling like:', err);
    }
  };

  const handleFork = async (id: string) => {
    if (!user) return;
    
    try {
      const forkedItemId = await interactionService.forkShowcaseItem(id, user.id);
      // Redirect to the new forked item
      window.location.href = `/showcase/${forkedItemId}`;
    } catch (err) {
      console.error('Error forking item:', err);
    }
  };

  const handleView = async (id: string) => {
    try {
      // Record view interaction
      if (user) {
        await interactionService.recordInteraction({
          userId: user.id,
          itemId: id,
          interactionType: 'view',
        });
      }
      
      // Increment view count
      await showcaseService.incrementViewCount(id);
    } catch (err) {
      console.error('Error recording view:', err);
    }
  };

  // Common filter tags used in the project
  const commonTags = [
    'education', 'productivity', 'creative', 'coding', 'writing', 
    'health', 'finance', 'entertainment', 'business', 'research'
  ];

  // Common capabilities
  const commonCapabilities = [
    'language processing', 'code generation', 'data analysis', 
    'content creation', 'tutoring', 'memory', 'planning'
  ];

  // Calculate current page
  const currentPage = Math.floor(filters.offset / filters.limit) + 1;
  const totalPages = Math.ceil(total / filters.limit);

  // Render loading state
  if (loading && items.length === 0) {
    return (
      <div className={cn("w-full p-8", className)}>
        <div className="animate-pulse space-y-6">
          <div className="flex justify-between items-center">
            <div className="h-10 w-48 bg-muted rounded"></div>
            <div className="h-10 w-32 bg-muted rounded"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="border rounded-lg p-4 space-y-3">
                <div className="flex gap-4">
                  <div className="h-12 w-12 bg-muted rounded-full"></div>
                  <div className="space-y-2 flex-grow">
                    <div className="h-4 bg-muted rounded w-3/4"></div>
                    <div className="h-3 bg-muted rounded w-1/2"></div>
                  </div>
                </div>
                <div className="h-24 bg-muted rounded"></div>
                <div className="flex justify-between">
                  <div className="h-4 bg-muted rounded w-1/4"></div>
                  <div className="h-4 bg-muted rounded w-1/4"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className={cn("w-full p-8 flex flex-col items-center justify-center text-center", className)}>
        <div className="text-destructive max-w-md">
          <h3 className="text-lg font-medium mb-2">Error loading showcase items</h3>
          <p className="mb-4">{error}</p>
          <Button 
            variant="outline" 
            onClick={fetchItems}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("w-full", className)}>
      {/* Featured section */}
      {showFeatured && <FeaturedAgents className="mb-12" />}
      
      {/* Gallery header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <h2 className="text-2xl font-bold">Showcase Gallery</h2>
        
        <div className="flex gap-2">
          <Tabs 
            defaultValue={filters.itemType[0]} 
            className="mr-2"
            onValueChange={(value) => handleItemTypeChange(value as 'agent' | 'project' | 'template')}
          >
            <TabsList>
              <TabsTrigger value="agent">Agents</TabsTrigger>
              <TabsTrigger value="project">Projects</TabsTrigger>
              <TabsTrigger value="template">Templates</TabsTrigger>
            </TabsList>
          </Tabs>
          
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
            title={viewMode === 'grid' ? 'Switch to list view' : 'Switch to grid view'}
          >
            {viewMode === 'grid' ? <List className="h-4 w-4" /> : <Grid2x2 className="h-4 w-4" />}
          </Button>
          
          <Button 
            variant={showFilters ? "default" : "outline"}
            onClick={() => setShowFilters(!showFilters)}
          >
            <SlidersHorizontal className="h-4 w-4 mr-2" />
            Filters
            {Object.values(filters).flat().filter(Boolean).length > 3 && (
              <Badge className="ml-2 h-5 w-5 p-0 flex items-center justify-center">
                {Object.values(filters).flat().filter(Boolean).length - 2}
              </Badge>
            )}
          </Button>
        </div>
      </div>
      
      {/* Search and filter bar */}
      <div className="mb-6 space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-grow">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search showcase..."
              className="pl-8"
              value={filters.search}
              onChange={handleSearchChange}
            />
          </div>
          
          <Select 
            value={filters.sortBy} 
            onValueChange={handleSortChange}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="oldest">Oldest</SelectItem>
              <SelectItem value="popular">Most Popular</SelectItem>
              <SelectItem value="trending">Trending</SelectItem>
              <SelectItem value="rating">Highest Rated</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Expanded filters */}
        {showFilters && (
          <div className="border rounded-lg p-4 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-medium">Filters</h3>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleResetFilters}
                className="h-8 text-sm"
              >
                Reset filters
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Tags filter */}
              <div>
                <h4 className="text-sm font-medium mb-2">Tags</h4>
                <div className="flex flex-wrap gap-2">
                  {commonTags.map(tag => (
                    <Badge 
                      key={tag} 
                      variant={filters.tags?.includes(tag) ? "default" : "outline"}
                      className="cursor-pointer hover:bg-accent/60"
                      onClick={() => handleTagToggle(tag)}
                    >
                      {tag}
                      {filters.tags?.includes(tag) && (
                        <X className="ml-1 h-3 w-3" />
                      )}
                    </Badge>
                  ))}
                </div>
              </div>
              
              {/* Capabilities filter */}
              <div>
                <h4 className="text-sm font-medium mb-2">Capabilities</h4>
                <div className="flex flex-wrap gap-2">
                  {commonCapabilities.map(capability => (
                    <Badge 
                      key={capability} 
                      variant={filters.capabilities?.includes(capability) ? "default" : "outline"}
                      className="cursor-pointer hover:bg-accent/60"
                      onClick={() => handleCapabilityToggle(capability)}
                    >
                      {capability}
                      {filters.capabilities?.includes(capability) && (
                        <X className="ml-1 h-3 w-3" />
                      )}
                    </Badge>
                  ))}
                </div>
              </div>
              
              {/* Complexity and featured filters */}
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium mb-2">Complexity</h4>
                  <div className="flex flex-wrap gap-2">
                    <Badge 
                      variant={filters.complexity?.includes('beginner') ? "default" : "outline"}
                      className="cursor-pointer hover:bg-accent/60 bg-green-100 text-green-800 hover:text-green-800"
                      onClick={() => handleComplexityToggle('beginner')}
                    >
                      Beginner
                      {filters.complexity?.includes('beginner') && (
                        <X className="ml-1 h-3 w-3" />
                      )}
                    </Badge>
                    <Badge 
                      variant={filters.complexity?.includes('intermediate') ? "default" : "outline"}
                      className="cursor-pointer hover:bg-accent/60 bg-amber-100 text-amber-800 hover:text-amber-800"
                      onClick={() => handleComplexityToggle('intermediate')}
                    >
                      Intermediate
                      {filters.complexity?.includes('intermediate') && (
                        <X className="ml-1 h-3 w-3" />
                      )}
                    </Badge>
                    <Badge 
                      variant={filters.complexity?.includes('advanced') ? "default" : "outline"}
                      className="cursor-pointer hover:bg-accent/60 bg-red-100 text-red-800 hover:text-red-800"
                      onClick={() => handleComplexityToggle('advanced')}
                    >
                      Advanced
                      {filters.complexity?.includes('advanced') && (
                        <X className="ml-1 h-3 w-3" />
                      )}
                    </Badge>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="featured" 
                    checked={filters.featured}
                    onCheckedChange={handleFeaturedToggle}
                  />
                  <label
                    htmlFor="featured"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    Featured items only
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Active filters */}
        {(filters.tags.length > 0 || 
          filters.capabilities.length > 0 || 
          filters.complexity.length > 0 || 
          filters.featured) && (
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-sm text-muted-foreground">Active filters:</span>
            
            {filters.tags.map(tag => (
              <Badge 
                key={`active-${tag}`} 
                variant="secondary"
                className="pl-2 pr-1 flex items-center gap-1"
              >
                {tag}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-4 w-4 p-0 hover:bg-accent/50"
                  onClick={() => handleTagToggle(tag)}
                >
                  <X className="h-3 w-3" />
                  <span className="sr-only">Remove {tag}</span>
                </Button>
              </Badge>
            ))}
            
            {filters.capabilities.map(capability => (
              <Badge 
                key={`active-${capability}`} 
                variant="secondary"
                className="pl-2 pr-1 flex items-center gap-1"
              >
                {capability}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-4 w-4 p-0 hover:bg-accent/50"
                  onClick={() => handleCapabilityToggle(capability)}
                >
                  <X className="h-3 w-3" />
                  <span className="sr-only">Remove {capability}</span>
                </Button>
              </Badge>
            ))}
            
            {filters.complexity.map(complexity => (
              <Badge 
                key={`active-${complexity}`} 
                variant="secondary"
                className="pl-2 pr-1 flex items-center gap-1"
              >
                {complexity}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-4 w-4 p-0 hover:bg-accent/50"
                  onClick={() => handleComplexityToggle(complexity)}
                >
                  <X className="h-3 w-3" />
                  <span className="sr-only">Remove {complexity}</span>
                </Button>
              </Badge>
            ))}
            
            {filters.featured && (
              <Badge 
                variant="secondary"
                className="pl-2 pr-1 flex items-center gap-1"
              >
                Featured
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-4 w-4 p-0 hover:bg-accent/50"
                  onClick={handleFeaturedToggle}
                >
                  <X className="h-3 w-3" />
                  <span className="sr-only">Remove featured filter</span>
                </Button>
              </Badge>
            )}
            
            <Button
              variant="ghost"
              size="sm"
              className="ml-2 h-7 text-xs"
              onClick={handleResetFilters}
            >
              Clear all
            </Button>
          </div>
        )}
      </div>
      
      {/* Results count */}
      <div className="mb-4 flex justify-between items-center">
        <p className="text-sm text-muted-foreground">
          Showing {items.length > 0 ? filters.offset + 1 : 0} - {Math.min(filters.offset + items.length, total)} of {total} items
        </p>
      </div>
      
      {/* Gallery grid/list */}
      {items.length > 0 ? (
        <>
          <div className={cn(
            viewMode === 'grid' 
              ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" 
              : "flex flex-col gap-4"
          )}>
            {items.map(item => (
              <AgentCard
                key={item.id}
                item={item}
                onLike={handleLike}
                onFork={handleFork}
                onView={handleView}
                isLiked={userLikes[item.id!]}
                className={viewMode === 'list' ? "flex flex-row h-auto" : ""}
              />
            ))}
          </div>
          
          {/* Pagination */}
          <div className="mt-8 flex justify-between items-center">
            <Button
              variant="outline"
              onClick={() => handlePageChange('prev')}
              disabled={filters.offset === 0}
            >
              <ChevronLeft className="mr-2 h-4 w-4" />
              Previous
            </Button>
            
            <span className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages || 1}
            </span>
            
            <Button
              variant="outline"
              onClick={() => handlePageChange('next')}
              disabled={filters.offset + filters.limit >= total}
            >
              Next
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </>
      ) : (
        <div className="min-h-[400px] flex flex-col items-center justify-center text-center p-8 border rounded-lg">
          <h3 className="text-lg font-medium mb-2">No items found</h3>
          <p className="text-muted-foreground mb-6 max-w-md">
            We couldn't find any showcase items matching your criteria. Try adjusting your filters or search terms.
          </p>
          <Button 
            variant="outline" 
            onClick={handleResetFilters}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Reset filters
          </Button>
        </div>
      )}
    </div>
  );
};